// La marca de estado — el `StatusMark` de React Bits que pasó Matías, portado a este repo
// (ADR-2026-10-04-status-mark-en-mejorar-prompt). Un anillo de rayitas (pendiente) que se
// FUNDE en un arco que gira (en curso) y termina en un anillo lleno con un tilde (listo) o
// una cruz (falló / cancelado). Cada cambio de `status` transforma el dibujo en el lugar.
//
// Sin `motion` (no está en el proyecto): los tres valores que el original anima con
// `useMotionValue` —`mode` (rayitas ↔ arco), `arc` (cuánto del anillo cubre) y `travel` (el
// giro)— viven en un ref y avanzan en un loop de `requestAnimationFrame` que corre sólo
// mientras hay algo en movimiento y escribe `stroke-dasharray` / `stroke-dashoffset` directo
// en el `<circle>`. El tilde, la cruz, los colores y el tachado son CSS, como en el original.
// Mismas props.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './StatusMark.css';

export type EstadoDeMarca = 'pending' | 'running' | 'done' | 'failed' | 'cancelled';

const CHECK = 'M7.5 12.25 10.5 15.25 16.75 8.75';
const CROSS = 'M8.5 8.5 15.5 15.5M15.5 8.5 8.5 15.5';
const TEXT: Record<EstadoDeMarca, string> = {
  pending: 'Pendiente',
  running: 'En curso',
  done: 'Listo',
  failed: 'Falló',
  cancelled: 'Cancelado',
};
const IDLE_DASH = 0.3;
/** Lo que dura cada transición (el `duration: 0.3` del original). */
const TRANSICION_MS = 300;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** `cubic-bezier(x1, y1, x2, y2)` como función de 0..1 (Newton sobre x, como los navegadores). */
function bezier(x1: number, y1: number, x2: number, y2: number): (t: number) => number {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const x = (t: number) => ((ax * t + bx) * t + cx) * t;
  const y = (t: number) => ((ay * t + by) * t + cy) * t;
  const dx = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (p) => {
    let t = p;
    for (let i = 0; i < 6; i++) {
      const d = dx(t);
      if (Math.abs(d) < 1e-6) break;
      t -= (x(t) - p) / d;
    }
    return y(clamp01(t));
  };
}
/** El `MORPH` del original. */
const SUAVE = bezier(0.77, 0, 0.175, 1);
/** El resorte sin rebote del original (`UI`), como una salida suave. */
const SALIDA = (t: number) => 1 - (1 - t) ** 3;

interface Tween {
  de: number;
  a: number;
  t0: number;
  curva: (t: number) => number;
  alTerminar?: () => void;
}

function useReducido(): boolean {
  const [r, setR] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const alCambiar = () => setR(mql.matches);
    alCambiar();
    mql.addEventListener('change', alCambiar);
    return () => mql.removeEventListener('change', alCambiar);
  }, []);
  return r;
}

export interface StatusMarkProps {
  status?: EstadoDeMarca;
  /** De 0 a 1 mientras corre. Sin esto, un arco que gira. */
  progress?: number;
  label?: React.ReactNode;
  color?: string;
  doneColor?: string;
  errorColor?: string;
  size?: number;
  strokeWidth?: number;
  dashes?: number;
  fontSize?: number;
  spinDuration?: number;
  arcLength?: number;
  drawDuration?: number;
  fillOpacity?: number;
  strike?: boolean;
  strikeDelay?: number;
  className?: string;
  style?: React.CSSProperties;
}

export default function StatusMark({
  status = 'pending',
  progress,
  label,
  color = 'currentColor',
  doneColor = '#22c55e',
  errorColor = '#ef4444',
  size = 20,
  strokeWidth = 2,
  dashes = 8,
  fontSize = 14,
  spinDuration = 1100,
  arcLength = 0.68,
  drawDuration = 240,
  fillOpacity = 0.06,
  strike = true,
  strikeDelay = 60,
  className = '',
  style,
}: StatusMarkProps) {
  const reduce = useReducido();
  const r = 10 - strokeWidth / 2;
  const C = 2 * Math.PI * r;
  const P = C / Math.max(1, dashes);
  const determinate = status === 'running' && Number.isFinite(progress);
  const indeterminate = status === 'running' && !determinate;
  const solid = status === 'running' || status === 'done' || status === 'failed';
  const targetArc = indeterminate ? arcLength : determinate ? clamp01(progress as number) : 1;

  const ringRef = useRef<SVGCircleElement | null>(null);
  const geo = useRef({ C, P });
  geo.current = { C, P };
  // Los tres valores animados y lo que los mueve, fuera de React.
  const v = useRef({ mode: solid ? 1 : 0, arc: targetArc, travel: 0 });
  const anim = useRef<{ mode?: Tween; arc?: Tween; travel?: Tween; gira: boolean; vuelta: number; raf: number; t: number }>({
    gira: false,
    vuelta: spinDuration,
    raf: 0,
    t: 0,
  });

  const escribir = () => {
    const g = geo.current;
    const { mode, arc, travel } = v.current;
    const dash = IDLE_DASH * g.P + (arc * g.C - IDLE_DASH * g.P) * mode;
    const gap = (1 - IDLE_DASH) * g.P + ((1 - arc) * g.C - (1 - IDLE_DASH) * g.P) * mode;
    const el = ringRef.current;
    if (!el) return;
    el.setAttribute('stroke-dasharray', `${Math.max(0, dash)} ${Math.max(0, gap)}`);
    el.setAttribute('stroke-dashoffset', String(travel));
  };

  const paso = (ahora: number) => {
    const a = anim.current;
    const dt = a.t ? ahora - a.t : 16;
    a.t = ahora;
    let vivo = a.gira;
    for (const clave of ['mode', 'arc', 'travel'] as const) {
      const tw = a[clave];
      if (!tw) continue;
      const p = clamp01((ahora - tw.t0) / TRANSICION_MS);
      v.current[clave] = tw.de + (tw.a - tw.de) * tw.curva(p);
      if (p >= 1) {
        a[clave] = undefined;
        tw.alTerminar?.();
      } else vivo = true;
    }
    if (a.gira) v.current.travel -= (geo.current.C * dt) / Math.max(1, a.vuelta);
    escribir();
    if (vivo) a.raf = window.requestAnimationFrame(paso);
    else {
      a.raf = 0;
      a.t = 0;
    }
  };
  const arrancar = () => {
    const a = anim.current;
    if (!a.raf) a.raf = window.requestAnimationFrame(paso);
  };

  useLayoutEffect(() => {
    escribir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [C, P]);

  // Al desmontar se corta el loop y se BORRA la marca de que corre: en StrictMode el
  // montaje doble dejaría el id viejo y `arrancar` creería que sigue vivo (pasó con el
  // WakeSlider, ADR-2026-10-03-wake-slider).
  useEffect(
    () => () => {
      const a = anim.current;
      if (a.raf) window.cancelAnimationFrame(a.raf);
      a.raf = 0;
      a.t = 0;
    },
    [],
  );

  useEffect(() => {
    const a = anim.current;
    const val = v.current;
    a.vuelta = spinDuration;
    if (reduce) {
      a.mode = a.arc = a.travel = undefined;
      a.gira = false;
      val.mode = solid ? 1 : 0;
      val.arc = targetArc;
      val.travel = 0;
      escribir();
      return;
    }
    const ahora = performance.now();
    // Desde las rayitas, el arco arranca ya con su largo: lo que se ve es la fusión.
    if (val.mode === 0) val.arc = targetArc;
    a.mode = { de: val.mode, a: solid ? 1 : 0, t0: ahora, curva: SUAVE };
    a.arc = { de: val.arc, a: targetArc, t0: ahora, curva: SALIDA };
    if (indeterminate) {
      a.travel = undefined;
      a.gira = true;
    } else {
      // Deja de girar y cae a la marca más cercana (una rayita, o la vuelta entera con
      // progreso), para que el anillo no pegue un salto; después vuelve a cero.
      a.gira = false;
      const unidad = determinate ? C : P;
      const hasta = Math.floor(val.travel / unidad) * unidad;
      a.travel = {
        de: val.travel,
        a: hasta,
        t0: ahora,
        curva: SALIDA,
        alTerminar: () => {
          v.current.travel = 0;
        },
      };
    }
    arrancar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, determinate, targetArc, reduce, C, P, spinDuration]);

  const spoken = TEXT[status] + (determinate ? `, ${Math.round(clamp01(progress as number) * 100)}%` : '');
  const hasLabel = label !== undefined && label !== null;

  return (
    <span
      className={`status-mark${className ? ` ${className}` : ''}`}
      data-status={status}
      data-indeterminate={indeterminate ? '' : undefined}
      data-strike={strike ? '' : undefined}
      style={
        {
          '--sm-size': `${size}px`,
          '--sm-stroke': strokeWidth,
          '--sm-color': color,
          '--sm-done': doneColor,
          '--sm-error': errorColor,
          '--sm-fill': fillOpacity,
          '--sm-font': `${fontSize}px`,
          '--sm-draw': `${drawDuration}ms`,
          '--sm-strike-delay': `${120 + strikeDelay}ms`,
          ...style,
        } as React.CSSProperties
      }
    >
      <svg
        className="status-mark__glyph"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        role={hasLabel ? undefined : 'img'}
        aria-label={hasLabel ? undefined : spoken}
        aria-hidden={hasLabel || undefined}
      >
        <circle className="status-mark__track" cx="12" cy="12" r={r} transform="rotate(-90 12 12)" />
        <circle ref={ringRef} className="status-mark__ring" cx="12" cy="12" r={r} transform="rotate(-90 12 12)" />
        <path className="status-mark__check" d={CHECK} pathLength={1} />
        <path className="status-mark__cross" d={CROSS} pathLength={1} />
      </svg>
      {hasLabel ? <span className="status-mark__sr">{spoken}: </span> : null}
      {hasLabel ? (
        <span className="status-mark__label">
          {label}
          <span className="status-mark__strike" aria-hidden="true" />
        </span>
      ) : null}
    </span>
  );
}
